import { makeStyles, tokens, Button } from '@fluentui/react-components';
import { BugRegular, LightbulbRegular } from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useFeedback } from '../feedback/FeedbackProvider';

const useStyles = makeStyles({
  // Page-background surface (Mission Control parity), starts after the rail —
  // it lives inside the content column in Layout, so it never underlaps the
  // rail. No copyright line, no footer logo (app-shell standard).
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    flexShrink: 0,
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingLeft: '16px',
    paddingRight: '16px',
    backgroundColor: 'var(--hts-app-bg)',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
  links: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  link: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    ':hover': { color: tokens.colorNeutralForeground2 },
  },
  version: {
    marginLeft: 'auto',
    fontSize: '12px',
    letterSpacing: '0.02em',
    color: tokens.colorNeutralForeground3,
  },
});

/**
 * Presentational footer shell (HTS app-shell standard): muted feedback links
 * left, build/version string right-aligned.
 *
 * Both links open the local FeedbackWizard (bug reports go to the GHE issue
 * pipeline; feature requests additionally mirror into Sentry User Feedback).
 */
function FooterChrome() {
  const styles = useStyles();
  const { openFeedback } = useFeedback();
  // Local-mode readout (native-shell dev loop): the bootloader serves the
  // app from the local dev server when reachable — the version flag makes
  // local code unmissable, and the live header-height readout proves the
  // safe-area inset actually applied on-device. Measured in an effect (not
  // render) — the header renders before the footer in Layout.
  const isLocal = typeof window !== 'undefined' && window.location.hostname === 'localhost';
  const [headerHeight, setHeaderHeight] = useState(0);
  useEffect(() => {
    if (!isLocal) return;
    // Deferred a frame: the header has laid out, and the lint rule against
    // synchronous setState-in-effect stays satisfied.
    const id = requestAnimationFrame(() => {
      setHeaderHeight(Math.round(document.querySelector('header')?.getBoundingClientRect().height ?? 0));
    });
    return () => cancelAnimationFrame(id);
  }, [isLocal]);

  return (
    <footer className={styles.footer}>
      <div className={styles.links}>
        <Button
          appearance="subtle"
          size="small"
          icon={<BugRegular />}
          className={styles.link}
          onClick={() => openFeedback('bug')}
        >
          Report Bug
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={<LightbulbRegular />}
          className={styles.link}
          onClick={() => openFeedback('feature')}
        >
          Request Feature
        </Button>
      </div>
      <span className={styles.version}>
        v{__APP_VERSION__}
        {isLocal ? ` — LOCAL MODE${headerHeight ? ` — H:${headerHeight}` : ''}` : ''}
      </span>
    </footer>
  );
}

/**
 * Footer for the app shell: the feedback links above open the local
 * FeedbackWizard (`FeedbackProvider` in App.tsx provides the context).
 */
export const Footer: React.FC = FooterChrome;

export default Footer;
